// /src/components/LiveNotification.tsx

import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { generateFomoMessage, FirmForFomo } from '../utils/fomoMessageGenerator';
import { propFirmData } from '../data/propFirmDataHomepage';

interface LiveNotificationProps {
  type?: 'discount' | 'social';
}

function calculateDiscountPercentage(firm: FirmForFomo): number | null {
  const fee = firm.evaluation?.fee;
  const discount = firm.evaluation?.discount?.[0];

  if (discount?.percent) {
    return discount.percent;
  }

  const discounted = discount?.discountedPrice;
  if (!fee || !discounted) return null;
  const discountPercent = ((fee - discounted) / fee) * 100;
  return Math.round(discountPercent);
}

function getRandomDelay(): number {
  // Shorter initial delay (5-15 seconds) for testing
  const initialDelay = Math.floor(Math.random() * (15000 - 5000) + 5000);
  // Regular delay (30-90 seconds) for subsequent messages
  const regularDelay = Math.floor(Math.random() * (90000 - 30000) + 30000);
  
  // Return shorter delay if no message has been shown yet
  return window.localStorage.getItem('firstMessageShown') ? regularDelay : initialDelay;
}

export default function LiveNotification({ type = 'discount' }: LiveNotificationProps) {
  const firms: FirmForFomo[] = Object.values(propFirmData);
  const [message, setMessage] = useState<{ text: string; link: string } | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  // Filter firms with active discounts
  const prioritizedFirms = firms.filter(
    (firm) => firm.evaluation?.discount?.[0]?.code && 
    (firm.evaluation?.discount?.[0]?.discountedPrice || firm.evaluation?.discount?.[0]?.percent)
  );

  const availableFirms = prioritizedFirms.length > 0 ? prioritizedFirms : firms;

  const generateMessage = useCallback(() => {
    const randomFirm = availableFirms[Math.floor(Math.random() * availableFirms.length)];
    const discountPercent = calculateDiscountPercentage(randomFirm);
    return generateFomoMessage({
      ...randomFirm,
      discountPercent,
      type
    });
  }, [availableFirms, type]);

  useEffect(() => {
    let showTimer: NodeJS.Timeout;
    let hideTimer: NodeJS.Timeout;

    const showMessage = () => {
      if (!isHovered) {
        const newMessage = generateMessage();
        setMessage(newMessage);
        console.log('Showing new message:', newMessage.text);

        // Mark first message as shown
        if (!window.localStorage.getItem('firstMessageShown')) {
          window.localStorage.setItem('firstMessageShown', 'true');
        }

        // Hide message after 7 seconds
        hideTimer = setTimeout(() => {
          setMessage(null);
          console.log('Hiding message');
          
          // Schedule next message
          const nextDelay = getRandomDelay();
          console.log('Next message in:', nextDelay / 1000, 'seconds');
          showTimer = setTimeout(showMessage, nextDelay);
        }, 7000);
      }
    };

    // Show first message after initial delay
    const initialDelay = getRandomDelay();
    console.log('Initial message in:', initialDelay / 1000, 'seconds');
    showTimer = setTimeout(showMessage, initialDelay);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [generateMessage, isHovered]);

  const notificationClass = type === 'discount' 
    ? "bg-white/5 backdrop-blur-lg border border-white/10 text-yellow-100 shadow-lg hover:shadow-yellow-400/10 hover:border-yellow-300/10"
    : "bg-white/5 backdrop-blur-lg border border-yellow-500/20 text-yellow-200 shadow-lg hover:shadow-yellow-400/10 hover:border-yellow-300/20";

  return (
    <div className="fixed bottom-4 left-4 md:left-6 z-50 max-w-xs w-full pointer-events-none">
      <AnimatePresence>
        {message && (
          <motion.div
            key={message.text}
            initial={{ opacity: 0, y: 5, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ 
              duration: 0.2,
              ease: 'easeOut',
              scale: {
                duration: 0.15
              }
            }}
            className={`${notificationClass} px-4 py-3 rounded-xl text-sm pointer-events-auto transition-all duration-200`}
            data-nosnippet
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <a 
              href={message.link}
              className="hover:text-yellow-300 duration-200 ease-in-out block"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {message.text}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
